import { type FC, useEffect, useState } from "react";
import { ToastTypeEnum, useToastCreate } from "@onshopfront/components";
import { useShopfrontDispatch } from "../../../../_Bootstrap/hooks";
import { hideDialog, showDialog } from "../../../../Actions/Dialogs/DialogActions";
import Button from "../../../../Common/Buttons/Button";
import { usePermissions } from "../../../../Utilities/Domain/Users/Permissions";
import FontAwesomeLight from "../../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import { OfflineSaleBackupManager } from "../../../../Utilities/Platform/OfflineSaleBackupManager";
import { ImportSaleBackup } from "./ImportSaleBackup";

const permissions = [ "modify_settings" ] as const;

export const OfflineSaleBackup: FC = () => {
    const { modifySettings } = usePermissions(permissions);

    const [ loading, setLoading ] = useState(true);
    const [ handle, setHandle ] = useState<FileSystemHandle | null>(null);

    const dispatch = useShopfrontDispatch();

    const createToast = useToastCreate();

    useEffect(() => {
        setLoading(true);

        OfflineSaleBackupManager.getInstance().loadBackupHandle()
            .then(setHandle)
            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if(!modifySettings) {
            dispatch(hideDialog());
        }
    }, [ modifySettings, dispatch ]);

    if(!modifySettings) {
        return null;
    }

    if(loading) {
        return (
            <div className="p-32 flex items-center justify-center text-lg">
                <FontAwesomeLight icon="spinner-third" /> Loading...
            </div>
        );
    }

    return (
        <>
            <div>
                {(handle === null ?
                    "Select the directory that the offline sales will be backed up in." :
                    `Offline sales are currently being backed up to "${handle.name}".`
                )}
            </div>
            <div className="flex flex-col items-center text-2xl mt-4 space-y-2">
                <Button
                    classes={[ "button-save w-full py-2" ]}
                    onClick={async () => {
                        try {
                            const newHandle = await window.showDirectoryPicker({
                                startIn: handle ?? undefined,
                            });

                            if(!newHandle) {
                                return;
                            }

                            await OfflineSaleBackupManager.getInstance().setBackupHandle(newHandle);

                            setHandle(handle);
                        } catch(err) {
                            console.error("Error selecting new handler", err);
                            createToast({
                                type   : ToastTypeEnum.Error,
                                message: "Unable to set new offline sales backup location",
                            });
                        }
                    }}
                >
                    <FontAwesomeLight icon="folder-open" />{handle === null ? "Set" : "Change"} Backup Folder
                </Button>
                <Button
                    classes={[ "button-save w-full py-2" ]}
                    onClick={async () => {
                        dispatch(showDialog(
                            "Import Backup Sales",
                            <ImportSaleBackup />,
                            "question"
                        ));
                    }}
                >
                    <FontAwesomeLight icon="file-import" />Restore Backup
                </Button>
            </div>
        </>
    );
};
